/**
 * Gestión → Administración (`/gestion/administracion`).
 *
 * El `index` del panel anterior era un **centro de administración** con tarjetas
 * hacia Documentos, Operaciones y Analytics. De esa pantalla se porta el analítico
 * que no depende de servicios externos: el **mapa de calor de surtidores**
 * (`Administration::pump_heatmap`), que muestra los despachos de un mes agrupados
 * por **día y hora** —en número de operaciones, importe o volumen— con los totales
 * por surtidor y los picos del periodo.
 *
 * El resto del centro de administración tiene su propia pantalla: los
 * **comprobantes** (`/gestion/administracion/comprobantes`), los **canjes masivos
 * de notas** (`/gestion/administracion/canjes-notas`) y el **consumo por
 * cliente** (`/gestion/administracion/consumo`).
 */

import { Flame, Info, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { Paginador } from '@/components/ui/paginador';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, cn } from '@/lib/utils';

import { AdministracionNav } from '../components/administracion-nav';
import { useMapaCalor } from '../cierre.api';
import { fechaHora } from '../gestion-formato';
import { METRICAS_CALOR, type MetricaCalor } from '../cierre-gestion.types';

const CONTROL =
  'h-11 rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Etiquetas de las métricas del mapa. */
const ETIQUETA_METRICA: Record<MetricaCalor, string> = {
  transactions: 'Operaciones',
  amount: 'Importe',
  volume: 'Volumen',
};

/** Número de días del mes. */
function diasDelMes(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/** Valor de una celda según la métrica. */
function valorDe(
  celda: { transacciones: number; monto: number; volumen: number } | undefined,
  metric: MetricaCalor,
): number {
  if (!celda) return 0;
  if (metric === 'amount') return celda.monto;
  if (metric === 'volume') return celda.volumen;

  return celda.transacciones;
}

/** Valor formateado según la métrica. */
function textoDe(valor: number, metric: MetricaCalor): string {
  if (metric === 'amount') return formatCurrency(valor);
  if (metric === 'volume') return `${valor.toLocaleString('es-PE', { maximumFractionDigits: 1 })} GLL`;

  return String(valor);
}

/** Color del mapa de calor (escala de la marca sobre el máximo del mes). */
function colorDe(valor: number, maximo: number): string {
  if (valor <= 0 || maximo <= 0) return 'transparent';

  const intensidad = Math.min(1, Math.max(0.08, valor / maximo));

  return `rgba(14, 116, 144, ${intensidad.toFixed(3)})`;
}

/** Intensidad (0-1) de una celda del mapa, para elegir el color del texto. */
function intensidadDe(valor: number, maximo: number): number {
  if (valor <= 0 || maximo <= 0) return 0;

  return Math.min(1, Math.max(0.08, valor / maximo));
}

/** Nombre corto del día de la semana (para el tooltip). */
function nombreDia(dia: number, year: number, month: number): string {
  const nombres = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const fecha = new Date(year, month - 1, dia);

  return nombres[fecha.getDay()] ?? '';
}

/** Valor de una fila de la matriz por lado según la métrica. */
function valorDeMatriz(
  fila: { transacciones: number; monto: number; volumen: number },
  metric: MetricaCalor,
): number {
  if (metric === 'amount') return fila.monto;
  if (metric === 'volume') return fila.volumen;

  return fila.transacciones;
}

export function AdministracionPage() {
  useDocumentTitle('Gestión · Administración');

  const hoy = useMemo(() => new Date(), []);
  const inicial = { year: hoy.getFullYear(), month: hoy.getMonth() + 1 };
  const [eleccion, setEleccion] = useState<{ year: number; month: number } | null>(null);
  const [metric, setMetric] = useState<MetricaCalor>('transactions');
  const [tooltip, setTooltip] = useState<{ x: number; y: number; dia: number; hora: number } | null>(null);

  // La consulta va siempre al periodo efectivo: el elegido por el usuario o, al
  // abrir, el último mes **con datos** (el mes en curso puede estar vacío).
  const seleccion = eleccion ?? inicial;
  const previa = useMapaCalor(seleccion.year, seleccion.month, metric);
  const ultimo = previa.data?.periodos?.[0];
  const periodo = eleccion ?? (ultimo ? { year: ultimo.year, month: ultimo.month } : inicial);

  const consulta = useMapaCalor(periodo.year, periodo.month, metric);
  const datos = consulta.data;
  const { year, month } = periodo;
  const periodos = datos?.periodos ?? [];

  // El detalle por surtidor se pagina; el mapa de calor muestra el mes completo.
  const paginacion = usePaginacion(datos?.surtidores ?? [], { clave: [year, month, metric] });

  const celdas = useMemo(() => {
    const mapa = new Map<string, { dia: number; hora: number; transacciones: number; monto: number; volumen: number }>();

    for (const celda of datos?.celdas ?? []) {
      mapa.set(`${celda.dia}-${celda.hora}`, celda);
    }

    return mapa;
  }, [datos]);

  const celdaActiva = tooltip ? celdas.get(`${tooltip.dia}-${tooltip.hora}`) : undefined;
  const ladosActivos = tooltip
    ? (datos?.matriz_surtidores ?? [])
        .filter((fila) => fila.dia === tooltip.dia && fila.hora === tooltip.hora)
        .sort((a, b) => a.surtidor - b.surtidor)
    : [];

  const totalesHoras = useMemo(() => {
    const totales = Array.from({ length: 24 }, () => 0);

    for (const celda of datos?.celdas ?? []) {
      totales[celda.hora] = (totales[celda.hora] ?? 0) + valorDe(celda, metric);
    }

    return totales;
  }, [datos, metric]);

  const totalesDias = useMemo(() => {
    const totales = new Map<number, number>();

    for (const celda of datos?.celdas ?? []) {
      totales.set(celda.dia, (totales.get(celda.dia) ?? 0) + valorDe(celda, metric));
    }

    return totales;
  }, [datos, metric]);

  const maximo = datos?.maximo ?? 0;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Administración</h1>
          <p className="mt-1 text-sm text-ink-500">
            Mapa de calor de despachos por día y hora de cada lado.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select
            className={CONTROL}
            value={String(year)}
            onChange={(evento) =>
              setEleccion({ year: Number(evento.target.value), month })
            }
            aria-label="Año"
          >
            {Array.from({ length: 4 }, (_, indice) => hoy.getFullYear() + 1 - indice).map((anio) => (
              <option key={anio} value={String(anio)}>
                {anio}
              </option>
            ))}
          </Select>

          <Select
            className={CONTROL}
            value={String(month)}
            onChange={(evento) => setEleccion({ year, month: Number(evento.target.value) })}
            aria-label="Mes"
          >
            {periodos
              .filter((item) => item.year === year)
              .map((item) => (
                <option key={`${item.year}-${item.month}`} value={String(item.month)}>
                  {item.etiqueta} ({item.filas})
                </option>
              ))}
            {!periodos.some((item) => item.year === year && item.month === month) ? (
              <option value={String(month)}>
                {datos?.periodo.etiqueta ?? month}
              </option>
            ) : null}
          </Select>

          <Select
            className={CONTROL}
            value={metric}
            onChange={(evento) => setMetric(evento.target.value as MetricaCalor)}
            aria-label="Métrica"
          >
            {METRICAS_CALOR.map((opcion) => (
              <option key={opcion} value={opcion}>
                {ETIQUETA_METRICA[opcion]}
              </option>
            ))}
          </Select>

          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <AdministracionNav active="calor" />

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el mapa de calor">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {consulta.isLoading ? <Skeleton className="h-96 w-full" /> : null}

      {datos ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
            {[
              {
                etiqueta: 'Operaciones',
                valor: datos.totales.transacciones.toLocaleString('es-PE'),
                detalle: `${datos.totales.dias} días con despachos`,
              },
              {
                etiqueta: 'Importe',
                valor: formatCurrency(datos.totales.monto),
                detalle: `Promedio ${formatCurrency(datos.totales.transacciones ? datos.totales.monto / datos.totales.transacciones : 0)} por operación`,
              },
              {
                etiqueta: 'Volumen',
                valor: `${datos.totales.volumen.toLocaleString('es-PE', { maximumFractionDigits: 1 })}`,
                detalle: 'Galones despachados',
              },
              {
                etiqueta: 'Lados',
                valor: String(datos.totales.surtidores),
                detalle: 'Con actividad en el mes',
              },
              {
                etiqueta: 'Pico del mes',
                valor: datos.totales.pico
                  ? textoDe(datos.totales.pico.valor, metric)
                  : '—',
                detalle: datos.totales.pico
                  ? `Día ${datos.totales.pico.dia} a las ${String(datos.totales.pico.hora).padStart(2, '0')}:00`
                  : 'Sin datos',
              },
            ].map((tarjeta) => (
              <Card key={tarjeta.etiqueta}>
                <CardContent className="p-4">
                  <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    {tarjeta.etiqueta}
                  </p>
                  <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
                  <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardContent className="space-y-3 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-800">
                  <Flame className="size-4 text-brand-500" aria-hidden="true" />
                  {datos.periodo.etiqueta} · {ETIQUETA_METRICA[metric]}
                </h2>
                <div className="flex items-center gap-2 text-xs text-ink-500">
                  <span>Menos</span>
                  <span className="flex gap-1">
                    {[0.1, 0.3, 0.55, 0.8, 1].map((nivel) => (
                      <span
                        key={nivel}
                        className="size-4 rounded"
                        style={{ backgroundColor: colorDe(nivel, 1) }}
                        aria-hidden="true"
                      />
                    ))}
                  </span>
                  <span>Más</span>
                </div>
              </div>

              {/*
                * Sin ancho mínimo: las 24 horas caben ajustando el relleno de las
                * celdas, así que el mapa de calor no obliga a desplazarse a la
                * derecha (el detalle de cada hora aparece en el tooltip).
                */}
              <div className="overflow-auto">
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr>
                      <th className="sticky left-0 z-10 bg-white px-2 py-1 text-left font-semibold text-ink-500">
                        Día
                      </th>
                      {Array.from({ length: 24 }, (_, hora) => (
                        <th
                          key={hora}
                          className="px-0 py-1 text-center font-semibold text-ink-500"
                          title={`${String(hora).padStart(2, '0')}:00`}
                        >
                          {hora}
                        </th>
                      ))}
                      <th className="px-2 py-1 text-right font-semibold text-ink-500">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: diasDelMes(year, month) }, (_, indice) => indice + 1).map(
                      (dia) => (
                        <tr key={dia}>
                          <td className="sticky left-0 z-10 bg-white px-2 py-1 font-semibold text-ink-700">
                            {dia}
                          </td>
                          {Array.from({ length: 24 }, (_, hora) => {
                            const celda = celdas.get(`${dia}-${hora}`);
                            const valor = valorDe(celda, metric);

                            return (
                              <td
                                key={hora}
                                className="p-0 text-center"
                                style={{ backgroundColor: colorDe(valor, maximo) }}
                                onMouseEnter={(evento) =>
                                  setTooltip({ x: evento.clientX, y: evento.clientY, dia, hora })
                                }
                                onMouseMove={(evento) =>
                                  setTooltip((previo) =>
                                    previo ? { ...previo, x: evento.clientX, y: evento.clientY } : previo,
                                  )
                                }
                                onMouseLeave={() => setTooltip(null)}
                              >
                                <span
                                  className={cn(
                                    'block px-0.5 py-0.5 text-[10px]',
                                    intensidadDe(valor, maximo) > 0.55
                                      ? 'font-semibold text-white'
                                      : 'text-ink-700',
                                  )}
                                >
                                  {valor > 0 ? Math.round(valor) : ''}
                                </span>
                              </td>
                            );
                          })}
                          <td className="px-2 py-1 text-right font-semibold whitespace-nowrap text-ink-700">
                            {textoDe(totalesDias.get(dia) ?? 0, metric)}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="sticky left-0 z-10 bg-white px-2 py-1 font-semibold text-ink-700">
                        Hora
                      </td>
                      {totalesHoras.map((total, hora) => (
                        <td
                          key={hora}
                          className="px-1 py-1 text-center font-semibold text-ink-600"
                          title={`${String(hora).padStart(2, '0')}:00`}
                        >
                          {total > 0 ? Math.round(total) : '—'}
                        </td>
                      ))}
                      <td className="px-2 py-1 text-right font-semibold text-ink-800">
                        {textoDe(totalesHoras.reduce((suma, total) => suma + total, 0), metric)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-4">
              <h2 className="text-sm font-semibold text-ink-800">Totales por lado</h2>

              {datos.surtidores.length === 0 ? (
                <p className="text-sm text-ink-500">Este mes no tiene despachos registrados.</p>
              ) : (
                <div className="overflow-auto rounded-card border border-ink-100">
                  <table className="w-full border-collapse text-[13px]">
                    <thead className="bg-white">
                      <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                        <th className="px-2 py-2 whitespace-nowrap">Lado</th>
                        <th className="px-2 py-2 text-right whitespace-nowrap">Operaciones</th>
                        <th className="px-2 py-2 text-right whitespace-nowrap">Importe</th>
                        <th className="px-2 py-2 text-right whitespace-nowrap">Volumen</th>
                        <th className="px-2 py-2 text-right whitespace-nowrap">Promedio</th>
                        <th className="px-2 py-2 text-center whitespace-nowrap">Días activos</th>
                        <th className="px-2 py-2 whitespace-nowrap">Última operación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginacion.visibles.map((surtidor) => (
                        <tr key={surtidor.surtidor} className="border-t border-ink-100">
                          <td className="px-2 py-2 font-semibold text-ink-900">
                            Lado {surtidor.surtidor}
                          </td>
                          <td className="px-2 py-2 text-right whitespace-nowrap">
                            {surtidor.transacciones.toLocaleString('es-PE')}
                          </td>
                          <td className="px-2 py-2 text-right whitespace-nowrap">
                            {formatCurrency(surtidor.monto)}
                          </td>
                          <td className="px-2 py-2 text-right whitespace-nowrap">
                            {surtidor.volumen.toLocaleString('es-PE', { maximumFractionDigits: 1 })}
                          </td>
                          <td className="px-2 py-2 text-right whitespace-nowrap">
                            {formatCurrency(
                              surtidor.transacciones ? surtidor.monto / surtidor.transacciones : 0,
                            )}
                          </td>
                          <td className="px-2 py-2 text-center whitespace-nowrap">
                            <Badge variant="neutral">{surtidor.dias_activos}</Badge>
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-xs text-ink-600">
                            {fechaHora(surtidor.ultima)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {datos.surtidores.length > 0 ? (
                <Paginador {...paginacion} etiqueta="lados" />
              ) : null}
            </CardContent>
          </Card>

          <p className="text-xs text-ink-500">
            Los meses con datos disponibles son los del selector; el mapa usa los despachos de
            `pts_pump_transactions` del mes elegido.
          </p>
        </>
      ) : null}

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        El mapa de calor reproduce el del panel anterior (día × hora por lado) y añade los
        totales por día, hora y lado con sus picos.
      </p>

      {tooltip ? (
        <div
          className="pointer-events-none fixed z-50 w-64 rounded-card border border-ink-200 bg-white p-3 text-xs shadow-xl"
          style={{ left: tooltip.x + 12, top: tooltip.y + 12 }}
        >
          <p className="font-semibold text-ink-800">
            {nombreDia(tooltip.dia, year, month)} {tooltip.dia} ·{' '}
            {String(tooltip.hora).padStart(2, '0')}:00
          </p>

          <div className="mt-2 space-y-1 text-ink-600">
            <p>
              Transacciones:{' '}
              <strong className="font-semibold text-ink-800">
                {(celdaActiva?.transacciones ?? 0).toLocaleString('es-PE')}
              </strong>
            </p>
            <p>
              Monto:{' '}
              <strong className="font-semibold text-ink-800">
                {formatCurrency(celdaActiva?.monto ?? 0)}
              </strong>
            </p>
            <p>
              Volumen:{' '}
              <strong className="font-semibold text-ink-800">
                {(celdaActiva?.volumen ?? 0).toLocaleString('es-PE', { maximumFractionDigits: 1 })} GL
              </strong>
            </p>
            <p>
              Promedio:{' '}
              <strong className="font-semibold text-ink-800">
                {formatCurrency(
                  (celdaActiva?.transacciones ?? 0) > 0
                    ? (celdaActiva?.monto ?? 0) / (celdaActiva?.transacciones ?? 1)
                    : 0,
                )}
              </strong>
            </p>
          </div>

          {ladosActivos.length > 0 ? (
            <div className="mt-2 space-y-1 border-t border-ink-100 pt-2 text-ink-600">
              {ladosActivos.map((fila) => (
                <p key={fila.surtidor}>
                  Lado {fila.surtidor}:{' '}
                  <strong className="font-semibold text-ink-800">
                    {textoDe(valorDeMatriz(fila, metric), metric)}
                  </strong>
                </p>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
